import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import API_BASE_URL, { SCHOOL_QUIZ_API_BASE_URL } from "../config";
import { fetchClassesThunk } from "../../components/chapter/redux/chapterThunk"; // adjust path if different
import {
  fetchClasses,
  setClassForm,
  fetchSubjects,
  addOrUpdateClasses,
  deleteClass,
} from "./redux/schoolQuizSlice";
import { setClasses } from "../chapter/redux/chapterSlice";

// use exactly the working selector
// const { classes = [], subjects = [] } = useSelector(
//   (state) => state.chapter || {}
// );

export default function Groups() {
  const dispatch = useDispatch();

  const { classesList, classesData, subjectsList } = useSelector(
    (state) => state.schoolQuiz
  );
  const classes = classesList?.data || [];
  // groups (themes)
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // modal & form state
  const [modalOpen, setModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [formName, setFormName] = useState("");
  const [selectedClassIds, setSelectedClassIds] = useState([]);
  // const [term,setTerm]
  // dropdown UI state
  const [ddOpen, setDdOpen] = useState(false);
  const ddRef = useRef(null);

  useEffect(() => {
    dispatch(fetchClasses());
    dispatch(fetchSubjects());
  }, [dispatch]);
  // fetch groups
  const fetchGroups = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${SCHOOL_QUIZ_API_BASE_URL}/groups`);
      setGroups(res.data || []);
    } catch (err) {
      setError("Failed to fetch group data");
      console.error("fetchGroups error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // fetch classes the same way your quiz page does
    // dispatch(fetchClassesThunk());
    fetchGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  // close dropdown when clicking outside
  useEffect(() => {
    function onDocClick(e) {
      if (ddRef.current && !ddRef.current.contains(e.target)) setDdOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  // helpers
  const idOf = (c) => c.id ?? c.class_id;
  const classLabel = (c) =>
    c.name || c.title || c.class_name || `Class ${idOf(c)}`;

  const openAddModal = () => {
    setModalOpen(true);
    setEditMode(false);
    setEditingGroup(null);
    setFormName("");
    setSelectedClassIds([]);
    setDdOpen(false);
  };

  const openEditModal = (group) => {
    console.log("group", group);
    setModalOpen(true);
    setEditMode(true);
    setEditingGroup(group);
    setFormName(group.group_name || "");

    if (Array.isArray(group.class_ids)) {
      setSelectedClassIds(group.class_ids.slice());
    } else if (Array.isArray(group.classes)) {
      setSelectedClassIds(group.classes.map((c) => idOf(c)));
    } else {
      setSelectedClassIds([]);
    }
    setDdOpen(false);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditMode(false);
    setEditingGroup(null);
    setDdOpen(false);
  };

  const toggleClass = (cid) => {
    setSelectedClassIds((prev) =>
      prev.includes(cid) ? prev.filter((i) => i !== cid) : [...prev, cid]
    );
  };

  const submitForm = async (e) => {
    e.preventDefault();
    if (!formName.trim()) return alert("Please enter group name.");
    setLoading(true);
    try {
      const payload = {
        group_name: formName.trim(),
        class_ids: selectedClassIds,
      };

      if (editMode && editingGroup) {
        await axios.put(
          `${SCHOOL_QUIZ_API_BASE_URL}/update-group/${editingGroup.group_id}`,
          payload
        );
      } else {
        await axios.post(`${SCHOOL_QUIZ_API_BASE_URL}/add-group`, payload);
      }
      await fetchGroups();
      closeModal();
    } catch (err) {
      alert(err.response?.data?.message || err.message || "Operation failed");
    } finally {
      setLoading(false);
    }
  };
  const submitFormNew = async (e) => {
    e.preventDefault();

    if (!classesData.class_name.trim()) {
      return alert("Please enter subject name.");
    }

    setLoading(true);
    console.log(classesData.subjects);
    try {
      const mode = classesData.mode;
      const class_id = mode !== "Add" ? classesData.class_id : null; // or whatever your editing ID is
      const class_name = classesData.class_name.trim();

      // 🔹 Dispatch the thunk
      const resultAction = await dispatch(
        addOrUpdateClasses({
          class_id,
          class_name,
          mode,
          subject_ids: classesData.subjects || [],
        })
      );

      // 🔹 Check if it succeeded
      if (addOrUpdateClasses.fulfilled.match(resultAction)) {
        dispatch(
          setClassForm({
            key: "open",
            value: false,
          })
        );
        await dispatch(fetchClasses()); // refresh list
      } else {
        alert(resultAction.payload || "Operation failed");
      }
    } catch (err) {
      console.error("submitFormNew error:", err);
      alert(err.message || "Operation failed");
    } finally {
      setLoading(false);
    }
  };
  const deleteGroup = async (g) => {
    if (!window.confirm?.(`Delete group "${g.name}"?`)) return;
    setLoading(true);
    try {
      await axios.delete(
        `${SCHOOL_QUIZ_API_BASE_URL}/delete-group/${g.group_id}`
      );
      await fetchGroups();
    } catch (err) {
      alert(err.response?.data?.message || err.message || "Delete failed");
    } finally {
      setLoading(false);
    }
  };

  // render selected labels (first two then +N)
  const renderSelectedSummary = () => {
    if (!selectedClassIds || selectedClassIds.length === 0)
      return "None selected";
    const names = selectedClassIds
      .map((id) => {
        const c = classes.find((x) => idOf(x) === id);
        return c ? classLabel(c) : `#${id}`;
      })
      .filter(Boolean);
    if (names.length <= 2) return names.join(", ");
    return `${names.slice(0, 2).join(", ")} +${names.length - 2} more`;
  };

  const renderSelectedSummaryNew = () => {
    const selected = classesData.subjects || [];

    if (selected.length === 0) return "None selected";

    const names = selected
      .map((id) => {
        const subj = subjectsList?.data?.find((s) => s.subject_id === id);
        return subj ? subj.subject_name : `#${id}`;
      })
      .filter(Boolean);

    if (names.length <= 2) return names.join(", ");
    return `${names.slice(0, 2).join(", ")} +${names.length - 2} more`;
  };

  // display mapping for table (g.class_ids or g.classes)
  const classObjectsFromGroup = (g) => {
    if (Array.isArray(g.classes) && g.classes.length) return g.classes;
    if (Array.isArray(g.class_ids) && g.class_ids.length)
      return g.class_ids.map((id) => ({
        id,
        name: (classes.find((x) => idOf(x) === id) || {}).name || `#${id}`,
      }));
    return [];
  };

  const toggleSubject = (subject_id) => {
    const currentSubjects = classesData.subjects || [];
    let updatedSubjects = [];

    if (currentSubjects.includes(subject_id)) {
      // Remove if already selected
      updatedSubjects = currentSubjects.filter((id) => id !== subject_id);
    } else {
      // Add if newly selected
      updatedSubjects = [...currentSubjects, subject_id];
    }

    // Update Redux store
    dispatch(setClassForm({ key: "subjects", value: updatedSubjects }));
  };
  return (
    <div className="container px-0">
      {/* Left Panel - Question Selection */}
      <div className="left-panel">
        <div>
          <div style={{ display: "flex", gap: "45%", marginLeft: "40%" }}>
            {/* <h5 style={{ textAlign: "center" }}>Groups</h5> */}

            <h5 style={{ textAlign: "center" }}> Groups</h5>
            <button
              className="btn btn-primary btn-sm"
              onClick={openAddModal}
              style={{ float: "right" }}
            >
              + Add Group
            </button>
          </div>
          <br />
          {error && <div className="alert alert-danger">{error}</div>}

          <div className="table-responsive">
            <table className="table mb-0 table-striped">
              <thead>
                <tr>
                  <th style={{ width: 80 }}>S.No</th>
                  <th>Group</th>
                  <th>Classes</th>
                  <th style={{ width: 160 }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {loading && groups.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="text-center p-4">
                      Loading...
                    </td>
                  </tr>
                ) : !loading && groups.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="text-center p-4">
                      No groups yet.
                    </td>
                  </tr>
                ) : (
                  groups?.data.map((g, i) => {
                    const classList = classObjectsFromGroup(g);

                    return (
                      <tr key={g.id ?? i}>
                        <td>{i + 1}</td>
                        <td>{g.group_name}</td>
                        <td>
                          {classList.length === 0 ? (
                            <small className="text-muted">—</small>
                          ) : (
                            classList.map((c) => (
                              <span
                                key={c.id}
                                className="bg-light text-dark me-1"
                              >
                                {c.name}
                              </span>
                            ))
                          )}
                        </td>
                        <td>
                          <div className="d-flex gap-2">
                            <button
                              className="btn btn-sm btn-outline-secondary"
                              onClick={() => openEditModal(g)}
                            >
                              Edit
                            </button>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => deleteGroup(g)}
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Modal */}
          {modalOpen && (
            <div style={modalBackdrop}>
              <div style={modalDialog}>
                <h5 className="mb-3">
                  {editMode ? "Edit Group" : "Add Group"}
                </h5>
                <form onSubmit={submitForm}>
                  <div className="mb-2">
                    <label className="form-label small">Group Name</label>
                    <input
                      className="form-control"
                      value={formName}
                      // value={editingGroup?.group_name || ""}
                      onChange={(e) => setFormName(e.target.value)}
                      placeholder="Group name"
                    />
                  </div>

                  <div className="mb-3" ref={ddRef}>
                    <label className="form-label small d-block">
                      Include Classes
                    </label>

                    {/* Dropdown trigger */}
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => setDdOpen((s) => !s)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setDdOpen((s) => !s);
                        }
                      }}
                      className="form-control d-flex justify-content-between align-items-center"
                      style={{ cursor: "pointer", userSelect: "none" }}
                      aria-expanded={ddOpen}
                    >
                      <div
                        style={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {renderSelectedSummary()}
                      </div>
                      <div style={{ marginLeft: 8 }}>
                        <small className="text-muted">
                          {ddOpen ? "▲" : "▼"}
                        </small>
                      </div>
                    </div>

                    {/* Dropdown panel */}
                    {ddOpen && (
                      <div
                        style={{
                          border: "1px solid #e6e6e6",
                          maxHeight: 220,
                          overflow: "auto",
                          padding: 8,
                          background: "#fff",
                          marginTop: 6,
                          borderRadius: 6,
                          boxShadow: "0 6px 18px rgba(0,0,0,0.08)",
                          zIndex: 2000,
                        }}
                      >
                        {classes.length === 0 ? (
                          <div className="text-muted">
                            No classes available.
                          </div>
                        ) : (
                          classes.map((c) => {
                            const cid = idOf(c);
                            const checked = selectedClassIds.includes(cid);
                            return (
                              <label
                                key={cid}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 8,
                                  padding: "6px 4px",
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => toggleClass(cid)}
                                  aria-checked={checked}
                                />
                                <span style={{ fontSize: 14 }}>
                                  {classLabel(c)}
                                </span>
                              </label>
                            );
                          })
                        )}
                      </div>
                    )}
                    <div className="form-text">
                      Click to open list — click checkboxes to select multiple
                      classes.
                    </div>
                  </div>

                  <div className="d-flex justify-content-end gap-2 mt-3">
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={closeModal}
                    >
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary btn-sm">
                      {editMode ? "Save" : "Add"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="right-panel" style={{ height: "80vh", overflow: "auto" }}>
        <div style={{ display: "flex", gap: "45%", marginLeft: "40%" }}>
          <h5 style={{ textAlign: "center" }}>Classes</h5>
          <button
            className="btn btn-primary btn-sm"
            onClick={() => {
              dispatch(setClassForm({ key: "open", value: true }));
              dispatch(setClassForm({ key: "subjects", value: [] }));
              dispatch(setClassForm({ key: "class_name", value: "" }));
              dispatch(setClassForm({ key: "mode", value: "Add" }));
            }}
            style={{ float: "right" }}
          >
            + Add Class
          </button>
        </div>
        <div className="table-responsive">
          <table className="table mb-0 table-striped">
            <thead>
              <tr>
                <th style={{ width: 80 }}>S.No</th>
                <th>Class</th>
                <th> Subjects</th>
                <th style={{ width: 160 }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {loading && classes.length === 0 ? (
                <tr>
                  <td colSpan={4} className="text-center p-4">
                    Loading...
                  </td>
                </tr>
              ) : !loading && classes.length === 0 ? (
                <tr>
                  <td colSpan={4} className="text-center p-4">
                    No classes yet.
                  </td>
                </tr>
              ) : (
                classesList?.data?.map((g, i) => {
                  return (
                    <tr key={g.id ?? i}>
                      <td>{i + 1}</td>
                      <td>{g.class_name}</td>
                      <td>
                        {g.subjects?.length > 0
                          ? g.subjects.map((e) => e.subject_name).join(", ")
                          : "-"}{" "}
                      </td>
                      <td>
                        <div className="d-flex gap-2">
                          <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => {
                              dispatch(
                                setClassForm({ key: "open", value: true })
                              );
                              dispatch(
                                setClassForm({
                                  key: "class_name",
                                  value: g.class_name,
                                })
                              );
                              dispatch(
                                setClassForm({
                                  key: "class_id",
                                  value: g.class_id,
                                })
                              );
                              dispatch(
                                setClassForm({
                                  key: "subjects",
                                  value:
                                    g.subjects?.length > 0
                                      ? g.subjects.map((e) => e.subject_id)
                                      : [],
                                })
                              );

                              dispatch(
                                setClassForm({
                                  key: "mode",
                                  value: "Update",
                                })
                              );
                            }}
                          >
                            Edit
                          </button>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={async () => {
                              const class_id = g.class_id;
                              setLoading(true);

                              try {
                                await dispatch(deleteClass({ class_id }));
                                await dispatch(fetchClasses());
                              } catch (err) {
                                console.error("deletion  error:", err);
                                alert(err.message || "Operation failed");
                              } finally {
                                setLoading(false);
                              }
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
      {classesData.open && (
        <div style={modalBackdrop}>
          <div style={modalDialog}>
            <h5 className="mb-3">{classesData.mode} Class</h5>
            <form onSubmit={submitFormNew}>
              <div className="mb-2">
                <label className="form-label small">Class Name</label>
                <input
                  className="form-control"
                  value={classesData.class_name || ""}
                  onChange={(e) =>
                    dispatch(
                      setClassForm({ key: "class_name", value: e.target.value })
                    )
                  }
                  placeholder="Class name"
                />
              </div>

              <div className="mb-3" ref={ddRef}>
                <label className="form-label small d-block">
                  Include Subjects
                </label>

                {/* Dropdown trigger */}
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => setDdOpen((s) => !s)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setDdOpen((s) => !s);
                    }
                  }}
                  className="form-control d-flex justify-content-between align-items-center"
                  style={{ cursor: "pointer", userSelect: "none" }}
                  aria-expanded={ddOpen}
                >
                  <div
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {renderSelectedSummaryNew()}
                  </div>
                  <div style={{ marginLeft: 8 }}>
                    <small className="text-muted">{ddOpen ? "▲" : "▼"}</small>
                  </div>
                </div>

                {/* Dropdown panel */}
                {ddOpen && (
                  <div
                    style={{
                      border: "1px solid #e6e6e6",
                      maxHeight: 220,
                      overflow: "auto",
                      padding: 8,
                      background: "#fff",
                      marginTop: 6,
                      borderRadius: 6,
                      boxShadow: "0 6px 18px rgba(0,0,0,0.08)",
                      zIndex: 2000,
                    }}
                  >
                    {subjectsList.length === 0 ? (
                      <div className="text-muted">No subjects available.</div>
                    ) : (
                      subjectsList?.data?.map((c) => {
                        const sid = c.subject_id; // assuming subject_id is the key
                        const checked = classesData.subjects?.includes(sid);

                        return (
                          <label
                            key={sid}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                              padding: "6px 4px",
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleSubject(sid)}
                              aria-checked={checked}
                            />
                            <span style={{ fontSize: 14 }}>
                              {c.subject_name}
                            </span>
                          </label>
                        );
                      })
                    )}
                  </div>
                )}
                <div className="form-text">
                  Click to open list — click checkboxes to select multiple
                  subjects.
                </div>
              </div>

              <div className="d-flex justify-content-end gap-2 mt-3">
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => {
                    dispatch(
                      setClassForm({
                        key: "open",
                        value: false,
                      })
                    );
                  }}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary btn-sm">
                  {classesData.mode}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// styles
const modalBackdrop = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.32)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1200,
  padding: 12,
};

const modalDialog = {
  width: "100%",
  maxWidth: 720,
  background: "#fff",
  padding: 16,
  borderRadius: 6,
  boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
};
